import { Code, ConnectError } from '@connectrpc/connect'
import {
  type MasterDeleteBlockedByReferences,
  MasterDeleteBlockedByReferencesSchema,
  MasterReferrerKind,
} from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import type { TFunction } from 'i18next'

import { isNetworkError } from '~/libs/isNetworkError'

/**
 * MasterReferrerKind (index.md AC-MOI-009 「参照元種類」) → i18n label。
 */
function masterReferrerKindLabel(
  t: TFunction,
  kind: MasterReferrerKind,
): string {
  switch (kind) {
    case MasterReferrerKind.SUBMITTER_BG_MEMBER:
      return t(
        'page:adminOcctoMaster.common.masterReferrerKind.submitterBgMember',
      )
    case MasterReferrerKind.GENERATION_SALES_BG:
      return t(
        'page:adminOcctoMaster.common.masterReferrerKind.generationSalesBg',
      )
    case MasterReferrerKind.DEMAND_PROCUREMENT_BG:
      return t(
        'page:adminOcctoMaster.common.masterReferrerKind.demandProcurementBg',
      )
    case MasterReferrerKind.DEMAND_SUPPRESSION_SUPPRESSION_BG:
      return t(
        'page:adminOcctoMaster.common.masterReferrerKind.demandSuppressionSuppressionBg',
      )
    case MasterReferrerKind.DEMAND_SUPPRESSION_PARTNER_BG:
      return t(
        'page:adminOcctoMaster.common.masterReferrerKind.demandSuppressionPartnerBg',
      )
    case MasterReferrerKind.DEMAND_SUPPRESSION_MEMBER_BG:
      return t(
        'page:adminOcctoMaster.common.masterReferrerKind.demandSuppressionMemberBg',
      )
    case MasterReferrerKind.BG_MEMBER:
      return t('page:adminOcctoMaster.common.masterReferrerKind.bgMember')
    case MasterReferrerKind.SUBMITTER_MASTER:
      return t(
        'page:adminOcctoMaster.common.masterReferrerKind.submitterMaster',
      )
    case MasterReferrerKind.PLANT_MASTER:
      return t('page:adminOcctoMaster.common.masterReferrerKind.plantMaster')
    case MasterReferrerKind.PLANT_MEMBER:
      return t('page:adminOcctoMaster.common.masterReferrerKind.plantMember')
    case MasterReferrerKind.DEMAND_SUPPRESSION_CONTRACT_BUSINESS:
      return t(
        'page:adminOcctoMaster.common.masterReferrerKind.demandSuppressionContractBusiness',
      )
    case MasterReferrerKind.GENERATION_SALES_SUBMITTER:
      return t(
        'page:adminOcctoMaster.common.masterReferrerKind.generationSalesSubmitter',
      )
    case MasterReferrerKind.DEMAND_SUPPRESSION_SUBMITTER:
      return t(
        'page:adminOcctoMaster.common.masterReferrerKind.demandSuppressionSubmitter',
      )
    case MasterReferrerKind.BG_PLANT_MEMBERSHIP:
      return t(
        'page:adminOcctoMaster.common.masterReferrerKind.bgPlantMembership',
      )
    case MasterReferrerKind.GENERATION_SALES_PLANT:
      return t(
        'page:adminOcctoMaster.common.masterReferrerKind.generationSalesPlant',
      )
    case MasterReferrerKind.PLANT_ACTUAL_SLOT:
      return t(
        'page:adminOcctoMaster.common.masterReferrerKind.plantActualSlot',
      )
    case MasterReferrerKind.GENERATION_SALES_TRADING_PARTNER_CODE:
      return t(
        'page:adminOcctoMaster.common.masterReferrerKind.generationSalesTradingPartnerCode',
      )
    case MasterReferrerKind.DEMAND_PROCUREMENT_TRADING_PARTNER_CODE:
      return t(
        'page:adminOcctoMaster.common.masterReferrerKind.demandProcurementTradingPartnerCode',
      )
    case MasterReferrerKind.PLANT_UNIT:
      return t('page:adminOcctoMaster.common.masterReferrerKind.plantUnit')
    case MasterReferrerKind.SPLIT_SUPPLY_CONTRACT:
      return t(
        'page:adminOcctoMaster.common.masterReferrerKind.splitSupplyContract',
      )
    case MasterReferrerKind.UNSPECIFIED:
    default:
      return ''
  }
}

/**
 * ConnectError から MasterDeleteBlockedByReferences detail (index.md
 * AC-MOI-009) を抽出し、「「{名称}」は {参照元種類} N件 / … で使用中です。
 * 使用している登録から外してから削除してください」形式の文言を組み立てる。
 * detail が取れない場合は undefined を返す (呼び出し元は既存の汎用
 * failedPrecondition 文言 (AC-MOI-028) にフォールバックする = backward compat)。
 */
function buildDeleteBlockedMessage(
  t: TFunction,
  err: ConnectError,
): string | undefined {
  let details: MasterDeleteBlockedByReferences[]
  try {
    details = err.findDetails(MasterDeleteBlockedByReferencesSchema)
  } catch {
    return undefined
  }
  const detail = details[0]
  if (detail === undefined) {
    return undefined
  }
  if (detail.breakdown.length === 0) {
    // 防衛的分岐 (backend drift 対策): breakdown が空のまま届くと
    // 「「{名称}」は  で使用中です。…」のような不自然な文言になる。
    // 既存の汎用 failedPrecondition 文言にフォールバックする。
    return undefined
  }
  const breakdownByLabel = new Map<string, number>()
  for (const item of detail.breakdown) {
    const kindLabel = masterReferrerKindLabel(t, item.referrerKind)
    if (kindLabel === '') {
      continue
    }
    const current = breakdownByLabel.get(kindLabel) ?? 0
    breakdownByLabel.set(kindLabel, current + item.count)
  }
  const breakdown = Array.from(breakdownByLabel.entries())
    .map(([kindLabel, count]) =>
      t('page:adminOcctoMaster.common.masterReferenceBreakdownItem', {
        kind: kindLabel,
        count,
      }),
    )
    .join(' / ')
  return t('page:adminOcctoMaster.common.masterDeleteBlockedByReferences', {
    name: detail.subjectDisplayName,
    breakdown,
  })
}

/**
 * OCCTO マスタ各タブの Delete 操作で共通の ConnectError → エラーメッセージ変換。
 *
 * 旧来は 5 マスタ (取引先 / 計画提出者 / BG / 事業者 / 発電所) の削除処理がそれぞれ同一の
 * switch(err.code) を持っていた。 `errorKeyPrefix` に各マスタの
 * `page:adminOcctoMaster.<master>.serverError` を渡すことで、 aborted /
 * failedPrecondition / notFound / unknown の 4 文言を引き分ける。
 *
 * FailedPrecondition 時は MasterDeleteBlockedByReferences detail
 * (index.md AC-MOI-009) が付いていれば参照元種類の内訳付き文言を組み立て、
 * detail が無い (= 旧来の BFF / backend や TradingPartner 等 detail 未対応
 * マスタ) 場合は `<errorKeyPrefix>.failedPrecondition` の汎用文言にフォール
 * バックする。
 */
export function mapMasterDeleteError(
  t: TFunction,
  err: unknown,
  errorKeyPrefix: string,
): string {
  if (err instanceof ConnectError) {
    if (isNetworkError(err)) {
      // AC-MOI-025: 通信エラーは削除専用の文言にする (`<prefix>.unknown` は保存の文言)。
      return t('common:connectError.deleteFailed')
    }
    switch (err.code) {
      case Code.Aborted:
        return t(`${errorKeyPrefix}.aborted`)
      case Code.FailedPrecondition:
        return (
          buildDeleteBlockedMessage(t, err) ??
          t(`${errorKeyPrefix}.failedPrecondition`)
        )
      case Code.NotFound:
        return t(`${errorKeyPrefix}.notFound`)
      default:
        return t(`${errorKeyPrefix}.unknown`)
    }
  }
  return t(`${errorKeyPrefix}.unknown`)
}

/**
 * 削除エラーが「同じ dialog から再試行して意味があるか」を判定する。
 *
 * - false (再試行不可): FailedPrecondition (FK 参照ブロック、 参照側を先に
 *   外さない限り同じエラーで弾かれる) / Aborted (楽観ロック conflict、
 *   最新の updated_at で一覧再読込が必要) / NotFound (対象データ消失、
 *   もう削除できない)
 * - true (再試行可能): 一時的失敗 (Unavailable / Internal / DeadlineExceeded /
 *   Unknown 等の default case、 および非 ConnectError の通信エラー)。
 *   dialog 内から即座に再試行することが有意
 *
 * DeleteConfirmDialog はこのフラグを `errorIsRetriable` prop で受け取り、
 * false のときのみ削除ボタンを非表示にする (キャンセルボタンの label は
 * 「閉じる」に切替、 index.md AC-MOI-009 の UX 補完と regression 防止)。
 */
export function isRetriableDeleteError(err: unknown): boolean {
  if (err instanceof ConnectError) {
    switch (err.code) {
      case Code.Aborted:
      case Code.FailedPrecondition:
      case Code.NotFound:
        return false
      default:
        return true
    }
  }
  return true
}
